/**
 * Per-diem calculation, shared verbatim by the phone (live preview) and the
 * API (authoritative persist).
 *
 * LOAD-BEARING — the order of operations:
 *
 *   amount = proration_percent x ( sum of the meal components still claimed )
 *
 * Two dimensions that look like one and are not:
 *
 *   1. Proration. GSA/IRS convention pays 75% of M&IE on the first and last
 *      travel day regardless of what time you left. That is a property of the
 *      DAY.
 *   2. Meal exclusion. A conference- or hotel-provided lunch is deducted at
 *      FULL value, because the traveller did not pay for it at all. That is a
 *      property of the MEAL.
 *
 * The booleans therefore sit INSIDE the parenthesis and the percent OUTSIDE.
 * Reverse them and a 75% day with a provided lunch over-reimburses by 25% of
 * the lunch rate -- small per day, systematic across every trip, and precisely
 * the kind of thing an auditor finds. This is why the rate is stored as four
 * separate meal components rather than one daily number: you cannot subtract a
 * lunch from a number that never had a lunch in it.
 */
import { addCents, applyPercent, toCents, type Cents } from './money';

export type PerDiemDayType = 'full' | 'first_day' | 'last_day' | 'single_day';

/** The rate components, as decimal strings straight out of `numeric` columns. */
export interface PerDiemRateComponents {
  breakfastAmount: string;
  lunchAmount: string;
  dinnerAmount: string;
  incidentalsAmount: string;
  /** Applied on first_day and last_day. Typically "0.7500". */
  firstLastDayPercent: string;
  /** Applied on a single-day trip with no overnight. Often "0.0000". */
  singleDayPercent: string;
}

export interface PerDiemClaim {
  breakfastClaimed: boolean;
  lunchClaimed: boolean;
  dinnerClaimed: boolean;
  incidentalsClaimed: boolean;
}

export interface PerDiemDayInput extends PerDiemClaim {
  dayType: PerDiemDayType;
}

export interface PerDiemDayResult {
  dayType: PerDiemDayType;
  /** The percent actually applied, as a decimal string, frozen onto the row. */
  prorationPercent: string;
  /** Sum of claimed components before proration. */
  claimedSubtotal: Cents;
  /** Final payable amount for the day. */
  amount: Cents;
}

export function prorationPercentFor(dayType: PerDiemDayType, rate: PerDiemRateComponents): string {
  switch (dayType) {
    case 'full':
      return '1.0000';
    case 'first_day':
    case 'last_day':
      return rate.firstLastDayPercent;
    case 'single_day':
      return rate.singleDayPercent;
  }
}

export function calculatePerDiemDay(
  input: PerDiemDayInput,
  rate: PerDiemRateComponents,
): PerDiemDayResult {
  // Step 1 -- meal exclusion, at FULL value.
  const claimedSubtotal = addCents(
    input.breakfastClaimed ? toCents(rate.breakfastAmount) : 0,
    input.lunchClaimed ? toCents(rate.lunchAmount) : 0,
    input.dinnerClaimed ? toCents(rate.dinnerAmount) : 0,
    input.incidentalsClaimed ? toCents(rate.incidentalsAmount) : 0,
  );

  // Step 2 -- proration, applied to what remains.
  const prorationPercent = prorationPercentFor(input.dayType, rate);
  const amount = applyPercent(claimedSubtotal, prorationPercent);

  return { dayType: input.dayType, prorationPercent, claimedSubtotal, amount };
}

/**
 * Propose one day per date in [startDate, endDate] inclusive.
 * A one-day trip is `single_day`, not `first_day` -- there is no overnight, so
 * the single-day rule applies and many employers pay nothing at all.
 * Dates are plain YYYY-MM-DD calendar strings; no timezone is involved, which
 * is why trips store `date` and not `timestamptz`.
 */
export function generatePerDiemDays(
  startDate: string,
  endDate: string,
  rate: PerDiemRateComponents,
  defaults: PerDiemClaim = {
    breakfastClaimed: true,
    lunchClaimed: true,
    dinnerClaimed: true,
    incidentalsClaimed: true,
  },
): Array<{ entryDate: string } & PerDiemDayInput & PerDiemDayResult> {
  const dates = eachDate(startDate, endDate);
  if (dates.length === 0) return [];

  return dates.map((entryDate, i) => {
    const dayType: PerDiemDayType =
      dates.length === 1 ? 'single_day' : i === 0 ? 'first_day' : i === dates.length - 1 ? 'last_day' : 'full';
    const input: PerDiemDayInput = { dayType, ...defaults };
    return { entryDate, ...input, ...calculatePerDiemDay(input, rate) };
  });
}

/** Inclusive date range over plain calendar dates, UTC-anchored to avoid DST drift. */
export function eachDate(startDate: string, endDate: string): string[] {
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];

  const out: string[] = [];
  const DAY = 86_400_000;
  for (let t = start; t <= end; t += DAY) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}
